<template>
	<Dialog
		class="new-relation-form"
		modal
		:header="`Add ${props.module.displayName} Relation`"
		:visible
		@update:visible="(reset(), emit('update:visible', $event))">
		<form
			class="d-flex"
			@submit.prevent="handleCreate">
			<div class="new-relation-form__form-content-container">
				<div class="new-relation-form__background-type-line" />
				<div class="new-relation-form__module-container">
					<div class="new-relation-form__module-header">
						<i
							class="new-relation-form__module-icon"
							:class="props.module.icon"></i>
						<div class="new-relation-form__module-name">{{ props.module.displayName }}</div>
					</div>
					<div class="form__input-container">
						<label :for="`${uuid}-name`">Relation Name</label>
						<InputText
							v-if="!props.hideInputs.includes('name')"
							:id="`${uuid}-name`"
							ref="nameInput"
							v-model="name"
							pattern="[a-z]+((\d)|([A-Z0-9][a-z0-9]+))*([A-Z])?"
							title="Unique relation name in camelCase"
							:disabled="!!props.forceValues.name"
							:placeholder="relationNamePlaceholder(false)"
							@blur="name = anyToCamelCase(name)"
							@input="clearValidationErrors()"
							@keydown.enter="name = anyToCamelCase(name)" />
					</div>
				</div>
				<div class="new-relation-form__relation-type-container">
					<!--<Button
						class="new-relation-form__relation-type-button"
						:severity="selectedType === 'HasOne' ? 'primary' : 'secondary'"
						@click="setRelationType('HasOne')">
						<HO
							class="new-relation-form__relation-type-icon"
							:class="{
								'new-relation-form__relation-type-icon--selected': selectedType === 'HasOne',
							}" />
					</Button>-->
					<Button
						class="new-relation-form__relation-type-button"
						:severity="selectedType === 'HasAndBelongsToOne' ? 'primary' : 'secondary'"
						@click="setRelationType('HasAndBelongsToOne')">
						<HBO
							class="new-relation-form__relation-type-icon"
							:class="{
								'new-relation-form__relation-type-icon--selected':
									selectedType === 'HasAndBelongsToOne',
							}" />
					</Button>
					<Button
						class="new-relation-form__relation-type-button"
						:severity="selectedType === 'HasAndBelongsToMany' ? 'primary' : 'secondary'"
						@click="setRelationType('HasAndBelongsToMany')">
						<HBM
							class="new-relation-form__relation-type-icon"
							:class="{
								'new-relation-form__relation-type-icon--selected':
									selectedType === 'HasAndBelongsToMany',
							}" />
					</Button>
					<!--<Button
					label="BMHM"
					@click="setRelationType('BelongsToAndHasMany')"></Button>-->
					<!--<Button
						class="new-relation-form__relation-type-button"
						:severity="selectedType === 'HasMany' ? 'primary' : 'secondary'"
						@click="setRelationType('HasMany')">
						<HM
							class="new-relation-form__relation-type-icon"
							:class="{
								'new-relation-form__relation-type-icon--selected': selectedType === 'HasMany',
							}" />
					</Button>-->
					<Button
						class="new-relation-form__relation-type-button"
						:severity="selectedType === 'BelongsToMany' ? 'primary' : 'secondary'"
						@click="setRelationType('BelongsToMany')">
						<HMHM
							class="new-relation-form__relation-type-icon"
							:class="{
								'new-relation-form__relation-type-icon--selected': selectedType === 'BelongsToMany',
							}" />
					</Button>
				</div>
				<div class="new-relation-form__module-container">
					<div
						class="new-relation-form__module-header new-relation-form__module-header--no-padding">
						<Select
							v-if="!props.hideInputs.includes('relatedModule')"
							:id="`${uuid}-related-module`"
							v-model="relatedModuleName"
							class="new-relation-form__module-select"
							option-label="title"
							option-value="value"
							:disabled="!!props.forceValues.relatedModule"
							:options="modules"
							@change="(clearValidationErrors(), resetNames())">
							<template #value>
								<div class="new-relation-form__module-select-value">
									<i
										class="new-relation-form__module-select-value-icon"
										:class="`${relatedModule.icon}`"></i>
									<div
										class="new-relation-form__module-select-value-name"
										style="margin-left: 10px">
										{{ relatedModule.displayName }}
									</div>
								</div>
							</template>
							<template #option="slotProps">
								<FontAwesomeIcon
									:icon="app.modules.find((m) => m.name === slotProps.option.value)!.icon" />
								<div style="margin-left: 10px">
									{{ app.modules.find((m) => m.name === slotProps.option.value)!.displayName }}
								</div>
							</template>
						</Select>
					</div>
					<div class="form__input-container">
						<label :for="`${uuid}-inverse-name`">Inverse Relation Name</label>
						<InputText
							v-if="!props.hideInputs.includes('inverseName')"
							:id="`${uuid}-inverse-name`"
							ref="inverseNameInput"
							v-model="inverseName"
							pattern="[a-z]+((\d)|([A-Z0-9][a-z0-9]+))*([A-Z])?"
							title="Unique relation name in camelCase"
							:placeholder="relationNamePlaceholder(true)"
							@blur="inverseName = anyToCamelCase(inverseName)"
							@input="clearValidationErrors()"
							@keydown.enter="inverseName = anyToCamelCase(inverseName)" />
					</div>
				</div>
			</div>
			<RelationDiagram
				class="new-relation-form__relation-diagram"
				:inverse-module="relatedModule"
				:inverse-relation="compiledInverseRelation"
				:module
				:relation="compiledRelation" />
			<Button
				icon="fal fa-save"
				label="Create"
				type="submit"
				@submit.prevent="handleCreate" />
		</form>
	</Dialog>
</template>

<script setup lang="ts">
import {
	anyToCamelCase,
	pascalToCamelCase,
	anyToLabelCase,
} from '@codecannon/schemas/utils/strings'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Button from 'primevue/button'
import { computed, getCurrentInstance, ref, watch } from 'vue'
import RelationDiagram from './NewRelationForm/RelationDiagram.vue'
import HBM from '@/views/App/assets/HBM.vue'
import HBO from '@/views/App/assets/HBO.vue'
import HMHM from '@/views/App/assets/HMHM.vue'
import type { ComponentPublicInstance } from 'vue'
import type { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import type { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'
import { EnrichedRelation } from '@codecannon/schemas/schemas/EnrichedRelation'
import type { availableRelationTypes } from '@codecannon/schemas/interfaces/moduleSchema'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'

const emit = defineEmits<{
	(e: 'create', payload: { relation: EnrichedRelation; inverse: EnrichedRelation }): void
	(e: 'update:visible', value: boolean): void
}>()

const props = withDefaults(
	defineProps<{
		hideInputs?: string[]
		forceValues?: Record<string, any>
		module: EnrichedModule
		app: EnrichedApp
		visible: boolean
	}>(),
	{
		hideInputs: () => [],
		forceValues: () => ({}),
	},
)

const { modules, relatedModuleName, relatedModule } = useModules()
const { name, inverseName, nameInput, inverseNameInput } = useNames()
const { type, selectedType, setRelationType } = useRelationType()
const { validateInputs, clearValidationErrors } = useValidation()
const { compiledRelation, compiledInverseRelation } = useCompiledRelations()
const { uuid, reset, resetNames, relationNamePlaceholder } = useForm()

function handleCreate() {
	clearValidationErrors()
	if (!validateInputs()) return

	emit('create', {
		relation: compiledRelation.value,
		inverse: compiledInverseRelation.value,
	})
	reset()
}

function useForm() {
	const instance = getCurrentInstance()
	const uuid = ref(instance!.uid)

	function relationNamePlaceholder(inverse: boolean) {
		if (inverse) {
			if (type.value === 'BelongsToMany') {
				return `relationName (plural)`
			} else {
				return `relationName (singular)`
			}
		} else {
			if (type.value === 'BelongsToMany' || type.value === 'HasMany') {
				return `relationName (singular)`
			} else {
				return `relationName (plural)`
			}
		}
	}

	function reset() {
		type.value = 'HasMany'
		relatedModuleName.value = modules.value[0]!.value
		selectedType.value = 'HasAndBelongsToMany'
		resetNames()
	}

	function resetNames() {
		if (type.value === 'BelongsToMany' || type.value === 'HasMany') {
			name.value = pascalToCamelCase(relatedModule.value!.model.namePlural)
		} else {
			name.value = pascalToCamelCase(relatedModule.value!.model.name)
		}

		if (type.value === 'BelongsToMany') {
			inverseName.value = pascalToCamelCase(props.module.model.namePlural)
		} else {
			inverseName.value = pascalToCamelCase(props.module.model.name)
		}
	}

	watch(
		() => modules.value,
		() => {
			reset()
		},
		{ immediate: true },
	)
	watch(() => type.value, resetNames)
	watch(
		() => props.visible,
		() => reset(),
	)

	return {
		uuid,
		reset,
		resetNames,
		relationNamePlaceholder,
	}
}

function useNames() {
	const name = ref('')
	const inverseName = ref(`${props.module.name}s`)
	const nameInput = ref<ComponentPublicInstance<typeof InputText> | null>(null)
	const inverseNameInput = ref<ComponentPublicInstance<typeof InputText> | null>(null)

	return {
		name,
		inverseName,
		nameInput,
		inverseNameInput,
	}
}

function useRelationType() {
	const type = ref<(typeof availableRelationTypes)[number]>('HasMany')
	const selectedType = ref('HasAndBelongsToMany')

	function setRelationType(
		newType:
			| 'HasOne'
			| 'HasAndBelongsToOne'
			| 'HasAndBelongsToMany'
			//| 'BelongsToAndHasMany'
			| 'BelongsToMany'
			| 'HasMany',
	) {
		selectedType.value = newType
		if (newType === 'HasOne') {
			type.value = 'HasOne'
		} else if (newType === 'HasAndBelongsToOne') {
			type.value = 'HasOne'
		} else if (newType === 'HasAndBelongsToMany') {
			type.value = 'HasMany'
		} else if (newType === 'BelongsToMany') {
			type.value = 'BelongsToMany'
		} else if (newType === 'HasMany') {
			type.value = 'HasMany'
		}
		clearValidationErrors()
		resetNames()
	}

	return {
		type,
		selectedType,
		setRelationType,
	}
}

function useModules() {
	const relatedModuleName = ref('')

	const modules = computed(() => {
		return props.app.modules.map((module) => ({
			title: module.displayName,
			value: module.name,
		}))
	})

	const relatedModule = computed(() => {
		return props.app.modules.find((m) => m.name === relatedModuleName.value)!
	})

	return {
		modules,
		relatedModuleName,
		relatedModule,
	}
}

function useValidation() {
	const nameDoesntConflictWithRelations = computed(() => {
		return !props.module.model.relations[name.value]
	})

	const inverseNameDoesntConflictWithRelations = computed(() => {
		if (!relatedModule.value) return false
		return !relatedModule.value.model.relations[inverseName.value]
	})

	const nameDoesntConflictWithColumns = computed(() => {
		return !props.module.model.columns[name.value]
	})

	const inverseNameDoesntConflictWithColumns = computed(() => {
		if (!relatedModule.value) return false
		return !relatedModule.value.model.columns[inverseName.value]
	})

	function validateInputs() {
		let valid = true
		if (
			nameInput.value &&
			relatedModuleName.value === props.module.name &&
			name.value === inverseName.value
		) {
			nameInput.value.$el.setCustomValidity(
				'Relation and inverse relation must have different names when defined on the same module',
			)
			nameInput.value.$el.reportValidity()
			valid = false
		}
		if (nameInput.value && !nameDoesntConflictWithRelations.value) {
			nameInput.value.$el.setCustomValidity(
				`Relation with same name already exists on ${props.module.name} module`,
			)
			nameInput.value.$el.reportValidity()
			valid = false
		}
		if (inverseNameInput.value && !inverseNameDoesntConflictWithRelations.value) {
			inverseNameInput.value.$el.setCustomValidity(
				`Inverse relation name on inverse ${relatedModule.value!.name} module already exists`,
			)
			inverseNameInput.value.$el.reportValidity()
			valid = false
		}
		if (nameInput.value && !nameDoesntConflictWithColumns.value) {
			nameInput.value.$el.setCustomValidity(
				`Column with same name name already exists on ${props.module.name} module`,
			)
			nameInput.value.$el.reportValidity()
			valid = false
		}
		if (inverseNameInput.value && !inverseNameDoesntConflictWithColumns.value) {
			inverseNameInput.value.$el.setCustomValidity(
				`Column with same name on inverse ${relatedModule.value!.name} module already exists`,
			)
			inverseNameInput.value.$el.reportValidity()
			valid = false
		}
		return valid
	}

	function clearValidationErrors() {
		if (nameInput.value) {
			nameInput.value.$el.setCustomValidity('')
		}
		if (inverseNameInput.value) {
			inverseNameInput.value.$el.setCustomValidity('')
		}
	}

	return {
		nameDoesntConflictWithRelations,
		inverseNameDoesntConflictWithRelations,
		nameDoesntConflictWithColumns,
		inverseNameDoesntConflictWithColumns,
		validateInputs,
		clearValidationErrors,
	}
}

function useCompiledRelations() {
	const compiledRelation = computed(() => {
		return new EnrichedRelation({
			name: name.value,
			displayName: anyToLabelCase(name.value),
			relationType: type.value,
			model: relatedModuleName.value,
			inverse: inverseName.value,
		})
	})

	const compiledInverseRelation = computed(() => {
		let inverseRelation = new EnrichedRelation({
			name: inverseName.value,
			displayName: anyToLabelCase(inverseName.value),
			relationType: 'BelongsTo',
			model: props.module.name,
			inverse: name.value,
		})
		if (compiledRelation.value.relationType === 'BelongsToMany') {
			inverseRelation.relationType = 'BelongsToMany'
		}
		return inverseRelation
	})

	return {
		compiledRelation,
		compiledInverseRelation,
	}
}
</script>

<style lang="scss" scoped>
.new-relation-form {
	form {
		display: flex;
		flex-direction: column;
		gap: 20px;
		width: 900px;

		& > * {
			width: 100%;
		}

		.form__input-container {
			display: flex;
			flex-direction: column;
			gap: 5px;
			margin-bottom: 10px;
		}

		.new-relation-form__form-content-container {
			display: flex;
			gap: 10px;
			width: 100%;
			justify-content: space-between;
			align-items: center;
			height: 175px;
			position: relative;

			.new-relation-form__background-type-line {
				background: var(--p-primary-500);
				height: 2px;
				width: 100%;
				position: absolute;
				top: calc(50% - 1px);
				z-index: -1;
			}

			.new-relation-form__module-container {
				display: flex;
				flex-direction: column;
				gap: 10px;
				width: 250px;
				background: var(--p-surface-100);
				padding: 20px;
				border-radius: 10px;

				@media all {
					background: var(--p-surface-950);
				}

				.new-relation-form__module-header {
					display: flex;
					align-items: center;
					gap: 5px;
					background: var(--p-surface-0);
					border-radius: 5px;
					padding: 8px 12px;

					@media all {
						border: 1px solid var(--p-inputtext-border-color);
						background: var(--p-surface-950);
					}

					&--no-padding {
						padding: 0;
					}

					.new-relation-form__module-icon {
						font-size: 24px;
						color: var(--p-primary-500);
					}

					.new-relation-form__module-name {
						font-size: 18px;
						color: var(--p-primary-500);
					}

					.new-relation-form__module-select {
						width: 100%;

						.new-relation-form__module-select-value {
							display: flex;
							align-items: center;

							.new-relation-form__module-select-value-icon {
								font-size: 24px;
								color: var(--p-primary-500);
							}

							.new-relation-form__module-select-value-name {
								font-size: 18px;
								color: var(--p-primary-500);
							}
						}
					}
				}
			}

			.new-relation-form__relation-type-container {
				display: flex;
				gap: 10px;

				.new-relation-form__relation-type-button {
					padding: 8px;

					.new-relation-form__relation-type-icon {
						width: 30px;
						height: 30px;

						:deep(circle) {
							stroke: var(--p-primary-500);
						}

						:deep(rect),
						:deep(path) {
							fill: var(--p-primary-500);
						}

						&--selected {
							:deep(circle) {
								stroke: var(--p-surface-0);

								@media all {
									stroke: var(--p-surface-950);
								}
							}

							:deep(rect),
							:deep(path) {
								fill: var(--p-surface-0);

								@media all {
									fill: var(--p-surface-950);
								}
							}
						}
					}
				}
			}
		}

		.new-relation-form__relation-diagram {
			border: 1px solid var(--p-surface-200);
			border-radius: 10px;

			@media all {
				border: none;
			}
		}
	}
}
</style>
